import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  filterBranchTree,
  formatParentNodeValue,
} from "./branchTreeData";
import { GenericFormField } from "common/Components/GenericForm";
import {
  HierarchyChildren,
  HierarchyCircleIcon,
  HierarchyDropdownButton,
  HierarchyRoot,
  HierarchyRow,
  HierarchyTitleButton,
} from "styles/dataManagementStyles";
import {
  CurrentValueText,
  EmptyTreeText,
  HintText,
  SearchFieldWrap,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  TreeScrollBox,
} from "./SelectParentDialog.styles";

function TreeBranch({ node, depth, expanded, onToggle, onSelect }) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen = expanded[node.id] ?? depth < 2;

  return (
    <HierarchyRoot>
      <HierarchyRow>
        {hasChildren ? (
          <HierarchyDropdownButton
            aria-label={isOpen ? "collapse" : "expand"}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.id);
            }}
          >
            {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </HierarchyDropdownButton>
        ) : (
          <HierarchyCircleIcon />
        )}
        <HierarchyTitleButton onClick={() => onSelect(node)}>
          {formatParentNodeValue(node)}
        </HierarchyTitleButton>
      </HierarchyRow>
      {hasChildren && isOpen ? (
        <HierarchyChildren>
          {node.children.map((child) => (
            <TreeBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </HierarchyChildren>
      ) : null}
    </HierarchyRoot>
  );
}

TreeBranch.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  expanded: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default function SelectParentDialog({
  open,
  onClose,
  onConfirm,
  initialValue,
  treeRoot,
  loading,
}) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const filteredRoot = useMemo(
    () => filterBranchTree(treeRoot, search),
    [treeRoot, search]
  );

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (node) => {
    const numericId = Number(node?.nodeId);
    onConfirm({
      displayLabel: formatParentNodeValue(node),
      parentNodeId: Number.isFinite(numericId) ? numericId : null,
    });
    onClose();
  };

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="select-parent-title"
      data-testid="select-parent-dialog"
    >
      <StyledDialogTitle id="select-parent-title">
        Select parent
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent dividers>
        <SearchFieldWrap>
          <GenericFormField
            field={{
              name: "search",
              type: "text",
              label: "Search",
              placeholder: "Search...",
              compact: true,
              inputProps: { "data-testid": "select-parent-search" },
            }}
            value={search}
            onChange={(_name, value) => setSearch(value)}
          />
        </SearchFieldWrap>
        {initialValue ? (
          <CurrentValueText variant="caption" display="block">
            Current: {initialValue}
          </CurrentValueText>
        ) : null}
        <TreeScrollBox>
          {loading ? (
            <EmptyTreeText>Loading node parents...</EmptyTreeText>
          ) : !treeRoot ? (
            <EmptyTreeText>No branch hierarchy is available for this node type.</EmptyTreeText>
          ) : !filteredRoot ? (
            <EmptyTreeText>No branches match your search.</EmptyTreeText>
          ) : (
            <Box sx={{ p: "8px 12px" }}>
              {filteredRoot.id === "context-root"
                ? filteredRoot.children.map((child) => (
                    <TreeBranch
                      key={child.id}
                      node={child}
                      depth={0}
                      expanded={expanded}
                      onToggle={handleToggle}
                      onSelect={handleSelect}
                    />
                  ))
                : (
                  <TreeBranch
                    node={filteredRoot}
                    depth={0}
                    expanded={expanded}
                    onToggle={handleToggle}
                    onSelect={handleSelect}
                  />
                )}
            </Box>
          )}
        </TreeScrollBox>
        <HintText variant="caption" display="block">
          Select an existing branch node. New nodes cannot be added from this dialog.
        </HintText>
      </StyledDialogContent>
    </StyledDialog>
  );
}

SelectParentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  treeRoot: PropTypes.shape({
    id: PropTypes.string,
    nodeId: PropTypes.number,
    name: PropTypes.string,
    children: PropTypes.array,
  }),
  loading: PropTypes.bool,
};

SelectParentDialog.defaultProps = {
  initialValue: "",
  treeRoot: null,
  loading: false,
};
